// ═══════════════════════════════════════════════════════════════════
//  AVAILABILITY EDITOR MODEL (AB#781)
//
//  The pure half of Appointments → Availability: turning a slot plan as
//  the API reads it (AB#780) into a Mon–Fri grid of cells, editing those
//  cells, checking the result the way the server will, and building the
//  body the save sends. Nothing here renders or fetches, so every rule is
//  unit-tested (editorModel.test.ts).
//
//  A site grid's cell is unavailable, public, private or managed (the
//  planning doc's names). A host grid's cell is available or unavailable:
//  a host's schedule is one availability for every event type at the site.
//  The site's breaks are locked: always unavailable, never editable.
// ═══════════════════════════════════════════════════════════════════

import { rowAxis, wallClockMinutes } from "@/app/booking-calendar/lib/dayGrid"
import {
  type MinuteOfDay,
  SITE_TIME_ZONE,
  WEEKDAYS,
  zonedDateAndMinutes,
  zonedTimeToInstant,
} from "@/app/booking-calendar/lib/siteTime"
import { SLOT_TYPE_LABEL, SLOT_TYPES, type SlotType } from "@/app/booking-calendar/lib/slotTypes"
import type {
  HostSlotPlanDto,
  HostSlotPlanRequest,
  SiteSlotPlanCell,
  SiteSlotPlanDto,
  SiteSlotPlanRequest,
  SlotPlanResetRequest,
  SlotPlanWindow,
} from "../availabilityApi"

export type EditorKind = "site" | "host"

export type SiteCellState = "unavailable" | SlotType
export type HostCellState = "unavailable" | "available"
export type CellState = SiteCellState | HostCellState

/** What one click does to a site cell: unavailable → public → private → managed → unavailable. */
export const SITE_CYCLE: readonly SiteCellState[] = ["unavailable", ...SLOT_TYPES]

/** What one click does to a host cell. */
export const HOST_CYCLE: readonly HostCellState[] = ["unavailable", "available"]

/** How the grid and its legend name each state: the slot types by the calendar's own labels. */
export const CELL_STATE_LABEL: Record<CellState, string> = {
  unavailable: "Unavailable",
  available: "Available",
  ...SLOT_TYPE_LABEL,
}

/** The columns, as System.DayOfWeek numbers: Monday (1) to Friday (5). */
export const GRID_WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5]

/** "Monday" for 1 … "Friday" for 5 (WEEKDAYS is Monday-first, DayOfWeek is Sunday-first). */
export function weekdayLabel(weekday: number): string {
  return WEEKDAYS[(weekday + 6) % 7] ?? String(weekday)
}

// ── Decoding the wire ───────────────────────────────────────────────

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]

/** A DayOfWeek as sent — a number, or a name should a string converter ever be added. */
export function decodeWeekday(value: number | string | null | undefined): number | null {
  if (typeof value === "number") return Number.isInteger(value) && value >= 0 && value <= 6 ? value : null
  if (typeof value !== "string") return null
  if (/^\d$/.test(value)) return decodeWeekday(Number(value))
  const index = DAY_NAMES.indexOf(value.toLowerCase())
  return index >= 0 ? index : null
}

const SLOT_TYPE_BY_NUMBER: readonly SlotType[] = ["public", "private", "managed"]
const SLOT_TYPE_NUMBER: Record<SlotType, number> = { public: 0, private: 1, managed: 2 }

/** An AmsSlotType as sent: 0/1/2, or "public"/"private"/"managed". */
export function decodeSlotType(value: number | string | null | undefined): SlotType | null {
  if (typeof value === "number") return SLOT_TYPE_BY_NUMBER[value] ?? null
  if (typeof value !== "string") return null
  if (/^\d$/.test(value)) return decodeSlotType(Number(value))
  const lowered = value.toLowerCase() as SlotType
  return SLOT_TYPES.includes(lowered) ? lowered : null
}

export type SlotPlanJobStatus = "queued" | "running" | "succeeded" | "failed"

const JOB_STATUS_BY_NUMBER: readonly SlotPlanJobStatus[] = ["queued", "running", "succeeded", "failed"]

/** An AmsSlotPlanJobStatus as sent: 0–3, or its name in any case ("Succeeded" in the notification). */
export function decodeJobStatus(value: number | string | null | undefined): SlotPlanJobStatus | null {
  if (typeof value === "number") return JOB_STATUS_BY_NUMBER[value] ?? null
  if (typeof value !== "string") return null
  if (/^\d$/.test(value)) return decodeJobStatus(Number(value))
  const lowered = value.toLowerCase() as SlotPlanJobStatus
  return JOB_STATUS_BY_NUMBER.includes(lowered) ? lowered : null
}

/** Whether a job has stopped for good — nothing more to poll for. */
export function isFinished(status: SlotPlanJobStatus | null): boolean {
  return status === "succeeded" || status === "failed"
}

/**
 * "09:30:00" or "09:30" as minutes past midnight; null when unreadable. An exclusive `end` of
 * midnight ("00:00:00", and "24:00") reads as 1440, as SlotPlanGrid.EndMinutes does.
 */
export function timeToMinutes(time: string | null | undefined, isEnd = false): MinuteOfDay | null {
  const minutes = wallClockMinutes(time ?? undefined)
  if (minutes == null || minutes > 1440) return null
  return isEnd && minutes === 0 ? 1440 : minutes
}

/** 570 → "09:30:00", the TimeOnly the API reads. 1440 (an exclusive end of midnight) → "00:00:00". */
export function minutesToTime(minutes: MinuteOfDay): string {
  const normalized = minutes % 1440
  const hours = Math.floor(normalized / 60).toString().padStart(2, "0")
  const mins = (normalized % 60).toString().padStart(2, "0")
  return `${hours}:${mins}:00`
}

// ── The grid ────────────────────────────────────────────────────────

export interface WeekdaySpan {
  weekday: number
  startMinutes: MinuteOfDay
  endMinutes: MinuteOfDay
}

/** The grid a plan is drawn on, all of it from the GET: slot length, window, rows and breaks. */
export interface GridFrame {
  slotMinutes: number
  startMinutes: MinuteOfDay
  endMinutes: MinuteOfDay
  /** Each row's start: whole slots from the window's start that finish by its end. */
  rows: MinuteOfDay[]
  breaks: WeekdaySpan[]
}

function spanOf(window: SlotPlanWindow): WeekdaySpan | null {
  const weekday = decodeWeekday(window.weekday)
  const startMinutes = timeToMinutes(window.start)
  const endMinutes = timeToMinutes(window.end, true)
  if (weekday == null || startMinutes == null || endMinutes == null || endMinutes <= startMinutes) return null
  return { weekday, startMinutes, endMinutes }
}

/** The frame a plan's GET describes, or null when its window or slot length cannot be read. */
export function frameFor(dto: Pick<SiteSlotPlanDto, "slotMinutes" | "window" | "lockedBreaks">): GridFrame | null {
  const startMinutes = timeToMinutes(dto.window?.start)
  const endMinutes = timeToMinutes(dto.window?.end, true)
  if (!(dto.slotMinutes > 0) || startMinutes == null || endMinutes == null || endMinutes <= startMinutes) return null

  return {
    slotMinutes: dto.slotMinutes,
    startMinutes,
    endMinutes,
    rows: rowAxis(dto.slotMinutes, { startMinutes, endMinutes }),
    breaks: (dto.lockedBreaks ?? []).map(spanOf).filter((span): span is WeekdaySpan => span !== null),
  }
}

export function cellKey(weekday: number, startMinutes: MinuteOfDay): string {
  return `${weekday}@${startMinutes}`
}

/** The break a cell overlaps, if any. A cell on a break is locked. */
export function lockedBreakAt(frame: GridFrame, weekday: number, startMinutes: MinuteOfDay): WeekdaySpan | undefined {
  const endMinutes = startMinutes + frame.slotMinutes
  return frame.breaks.find(b => b.weekday === weekday && b.startMinutes < endMinutes && startMinutes < b.endMinutes)
}

export function isLocked(frame: GridFrame, weekday: number, startMinutes: MinuteOfDay): boolean {
  return lockedBreakAt(frame, weekday, startMinutes) !== undefined
}

function isGridCell(frame: GridFrame, weekday: number, startMinutes: MinuteOfDay): boolean {
  return GRID_WEEKDAYS.includes(weekday) && frame.rows.includes(startMinutes)
}

// ── The editor's state ──────────────────────────────────────────────

export interface EditorState {
  kind: EditorKind
  frame: GridFrame
  /** Open cells only, by cellKey; anything absent is unavailable. */
  cells: Record<string, CellState>
  /** The start date-time as the native input holds it, site-local "YYYY-MM-DDTHH:mm"; "" for now. */
  activeFrom: string
  /** Carried through unchanged from the GET on host plans: the editor has no input for it. Never sent for sites. */
  activeUntil: string | null
  /** As the number input holds it; "" for the team's limit. */
  maxPerDay: string
  version: number | null
}

/** "YYYY-MM-DDTHH:mm" on the site's clock → an instant; null for "" and anything unreadable. */
export function datetimeLocalToInstant(value: string, timeZone: string = SITE_TIME_ZONE): string | null {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[2])
  const minutes = Number(match[3])
  if (hours > 23 || minutes > 59) return null
  return zonedTimeToInstant(match[1]!, (hours * 60) + minutes, timeZone).toISOString()
}

/** An instant → the native input's "YYYY-MM-DDTHH:mm" on the site's clock; "" for none. */
export function instantToDatetimeLocal(instant: string | null | undefined, timeZone: string = SITE_TIME_ZONE): string {
  if (!instant || Number.isNaN(new Date(instant).getTime())) return ""
  const { date, minutes } = zonedDateAndMinutes(instant, timeZone)
  return `${date}T${minutesToTime(minutes).slice(0, 5)}`
}

export interface LoadedEditor {
  state: EditorState
  /** Cells the GET returned that are not on this grid (wrong day, misaligned, outside the window). */
  droppedCells: number
}

/** The editor's state for a plan as read, plus how many of its cells did not fit the grid. */
export function editorStateFrom(
  kind: EditorKind,
  dto: SiteSlotPlanDto | HostSlotPlanDto,
  timeZone: string = SITE_TIME_ZONE,
): LoadedEditor | null {
  const frame = frameFor(dto)
  if (!frame) return null

  const cells: Record<string, CellState> = {}
  let droppedCells = 0

  for (const cell of dto.cells ?? []) {
    const span = spanOf(cell)
    const state: CellState | null = kind === "site"
      ? decodeSlotType((cell as SiteSlotPlanCell).slotType)
      : "available"

    if (
      !span || !state
      || span.endMinutes - span.startMinutes !== frame.slotMinutes
      || !isGridCell(frame, span.weekday, span.startMinutes)
      || isLocked(frame, span.weekday, span.startMinutes)
    ) {
      droppedCells++
      continue
    }
    cells[cellKey(span.weekday, span.startMinutes)] = state
  }

  return {
    state: {
      kind,
      frame,
      cells,
      activeFrom: instantToDatetimeLocal(dto.activeFrom, timeZone),
      activeUntil: dto.activeUntil ?? null,
      maxPerDay: dto.maxPerDay == null ? "" : String(dto.maxPerDay),
      version: dto.version ?? null,
    },
    droppedCells,
  }
}

export function cellState(state: EditorState, weekday: number, startMinutes: MinuteOfDay): CellState {
  return state.cells[cellKey(weekday, startMinutes)] ?? "unavailable"
}

/** The state one click moves a cell to. */
export function nextCellState(kind: EditorKind, current: CellState): CellState {
  const cycle: readonly CellState[] = kind === "site" ? SITE_CYCLE : HOST_CYCLE
  const index = cycle.indexOf(current)
  return cycle[(index + 1) % cycle.length]!
}

/** One click on a cell. A locked break, or a cell not on the grid, is left as it is. */
export function cycleCell(state: EditorState, weekday: number, startMinutes: MinuteOfDay): EditorState {
  if (!isGridCell(state.frame, weekday, startMinutes) || isLocked(state.frame, weekday, startMinutes)) return state

  const key = cellKey(weekday, startMinutes)
  const next = nextCellState(state.kind, cellState(state, weekday, startMinutes))
  const cells = { ...state.cells }
  if (next === "unavailable") delete cells[key]
  else cells[key] = next
  return { ...state, cells }
}

/** "Make unavailable": every cell closed. The dates, limit and version stay as they are. */
export function makeUnavailable(state: EditorState): EditorState {
  return { ...state, cells: {} }
}

// ── Checking and sending ────────────────────────────────────────────

/** Max per day as the API takes it: null for blank, otherwise the number (validated separately). */
function maxPerDayValue(value: string): number | null {
  return value.trim() === "" ? null : Number(value)
}

/**
 * Everything the server's 400 would refuse, said before sending: the start date-time must read,
 * the end it carries must come after it, the limit must be a whole number of at least 1, and every
 * open cell must be one slot on the grid's Mon–Fri boundaries, inside the window and off the breaks.
 */
export function validate(state: EditorState, timeZone: string = SITE_TIME_ZONE, now: Date = new Date()): string[] {
  const errors: string[] = []

  const activeFrom = state.activeFrom.trim() === "" ? null : datetimeLocalToInstant(state.activeFrom, timeZone)
  if (state.activeFrom.trim() !== "" && activeFrom == null) {
    errors.push("The start date-time is not a date and time.")
  }

  if (state.kind === "host" && state.activeUntil) {
    const from = activeFrom ? new Date(activeFrom).getTime() : now.getTime()
    if (new Date(state.activeUntil).getTime() <= from) {
      errors.push("The start date-time must be before the plan's end date-time.")
    }
  }

  const max = maxPerDayValue(state.maxPerDay)
  if (max != null && (!Number.isInteger(max) || max < 1)) {
    errors.push("Max bookings per day must be a whole number of 1 or more, or blank for the team's limit.")
  }

  for (const key of Object.keys(state.cells)) {
    const [weekdayText, startText] = key.split("@")
    const weekday = Number(weekdayText)
    const start = Number(startText)
    const label = `${weekdayLabel(weekday)} ${minutesToTime(start).slice(0, 5)}`
    if (!GRID_WEEKDAYS.includes(weekday)) {
      errors.push(`${label} is outside the grid, which runs Monday to Friday.`)
    } else if (!state.frame.rows.includes(start)) {
      errors.push(`${label} is not one of the grid's ${state.frame.slotMinutes}-minute slots.`)
    } else if (isLocked(state.frame, weekday, start)) {
      errors.push(`${label} is on a break, which cannot be opened.`)
    }
  }

  return errors
}

function orderedCells(state: EditorState): { weekday: number; startMinutes: number; value: CellState }[] {
  return Object.entries(state.cells)
    .map(([key, value]) => {
      const [weekday, startMinutes] = key.split("@").map(Number)
      return { weekday: weekday!, startMinutes: startMinutes!, value }
    })
    .sort((a, b) => a.weekday - b.weekday || a.startMinutes - b.startMinutes)
}

/** The dates, limit and version as the API takes them. A site plan never carries an end (the API refuses one). */
function settings(state: EditorState, timeZone: string): SlotPlanResetRequest {
  return {
    activeFrom: datetimeLocalToInstant(state.activeFrom, timeZone),
    activeUntil: state.kind === "host" ? state.activeUntil : null,
    maxPerDay: maxPerDayValue(state.maxPerDay),
    version: state.version,
  }
}

/** The PUT body: the whole grid (absent cells are unavailable), the settings, and the version read. */
export function buildSaveRequest(state: EditorState, timeZone: string = SITE_TIME_ZONE): SiteSlotPlanRequest | HostSlotPlanRequest {
  const windows = orderedCells(state).map(cell => ({
    weekday: cell.weekday,
    start: minutesToTime(cell.startMinutes),
    end: minutesToTime(cell.startMinutes + state.frame.slotMinutes),
    value: cell.value,
  }))

  if (state.kind === "site") {
    return {
      ...settings(state, timeZone),
      cells: windows.map(({ value, ...window }) => ({ ...window, slotType: SLOT_TYPE_NUMBER[value as SlotType] })),
    }
  }
  return { ...settings(state, timeZone), cells: windows.map(({ value: _value, ...window }) => window) }
}

/** The body of "Restore default" (and the server's make-unavailable): the settings without a grid. */
export function buildResetRequest(state: EditorState, timeZone: string = SITE_TIME_ZONE): SlotPlanResetRequest {
  return settings(state, timeZone)
}

export interface CellDiff {
  /** Unavailable before, open now. */
  opened: number
  /** Open before, unavailable now. */
  closed: number
  /** Open both times as a different type (site grids only). */
  retyped: number
}

/** How the grid differs from the one read — the summary beside Submit. */
export function diffCells(before: EditorState, after: EditorState): CellDiff {
  let opened = 0
  let closed = 0
  let retyped = 0
  for (const [key, value] of Object.entries(after.cells)) {
    const was = before.cells[key]
    if (!was) opened++
    else if (was !== value) retyped++
  }
  for (const key of Object.keys(before.cells)) {
    if (!after.cells[key]) closed++
  }
  return { opened, closed, retyped }
}

/** Whether there is anything to submit. */
export function isDirty(before: EditorState, after: EditorState): boolean {
  const diff = diffCells(before, after)
  return diff.opened + diff.closed + diff.retyped > 0
    || before.activeFrom !== after.activeFrom
    || before.maxPerDay.trim() !== after.maxPerDay.trim()
}

/** "3 opened, 1 closed, 2 changed type" — or "" when the grid is unchanged. */
export function describeDiff(diff: CellDiff): string {
  const parts: string[] = []
  if (diff.opened) parts.push(`${diff.opened} opened`)
  if (diff.closed) parts.push(`${diff.closed} closed`)
  if (diff.retyped) parts.push(`${diff.retyped} changed type`)
  return parts.join(", ")
}

export interface SaveFailure {
  /** A 409: somebody saved the plan since it was read. Reload, then edit again. */
  conflict: boolean
  messages: string[]
}

/** The `code` of a 409 that refuses a host plan overlapping the host's hours at another site (AB#804). */
export const HOST_SITE_CONFLICT_CODE = "host-site-conflict"

/** One overlap a host-site 409 lists; `message` is the server's sentence for it. */
export interface HostSiteConflict {
  otherTeamSlug: string
  otherSiteName: string
  weekday: number
  otherStart: string
  otherEnd: string
  start: string
  end: string
  message: string
}

/** The body of a host-site 409 (SlotPlanConflictProblem). */
export interface SlotPlanConflictProblem {
  code: typeof HOST_SITE_CONFLICT_CODE
  message: string
  conflicts: HostSiteConflict[]
}

/** Whether a 409's body is the one-site-per-host refusal rather than the version check's sentence. */
export function isHostSiteConflict(data: unknown): data is SlotPlanConflictProblem {
  return typeof data === "object" && data !== null
    && (data as { code?: unknown }).code === HOST_SITE_CONFLICT_CODE
}

/**
 * What a refused save says, from the mutation's error: a 400's SlotPlanProblem lists every refused
 * cell or setting; a 409 with code "host-site-conflict" is the one-site-per-host rule (its message,
 * then each overlap — reloading would not help, so no reload is offered); any other 409 is the
 * version check (its body is the sentence); anything else is generic.
 */
export function describeSaveError(error: unknown): SaveFailure {
  const { status, data } = (error ?? {}) as { status?: number | string; data?: unknown }
  const errors = (data as { errors?: unknown } | null | undefined)?.errors

  if (status === 400 && Array.isArray(errors) && errors.length > 0) {
    return { conflict: false, messages: errors.map(String) }
  }
  if (status === 409 && isHostSiteConflict(data)) {
    const overlaps = Array.isArray(data.conflicts)
      ? data.conflicts.map(c => String(c?.message ?? "")).filter(m => m.trim() !== "")
      : []
    const lead = typeof data.message === "string" && data.message.trim() !== ""
      ? data.message
      : "This host is already available at another site at the same time."
    return { conflict: false, messages: [lead, ...overlaps] }
  }
  if (status === 409) {
    return {
      conflict: true,
      messages: [typeof data === "string" && data.trim() !== ""
        ? data
        : "The plan has changed since it was read. Reload it and try again."],
    }
  }
  if (status === 403) return { conflict: false, messages: ["You do not have permission to change availability."] }
  if (status === 404) return { conflict: false, messages: ["That schedule no longer exists. Pick it again."] }
  return { conflict: false, messages: ["The changes could not be saved. Try again in a moment."] }
}
